import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarPlus, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import { PageHeader } from '@/components/PageHeader';
import { LongExpiredToggle, StatusBadge } from '@/components/csp/CspKit';
import { exportIcs, useCspRows, worstStatus, type CspRow } from '@/lib/csp';
import { cspLicencesView } from '@/lib/routes';
import { cn } from '@/lib/utils';
import { EmptyDataset } from './CspDashboard';
import { useCompanies } from '@/api/insights';
import { OrbitLink } from '@/components/insights/OrbitLink';

/** CSP → Customers. One card per customer with its worst-case status; click for its licences. */
export default function CspCustomersPage() {
  const { query, dataset, all, visible } = useCspRows({ scoped: true });
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  // Report customer → Autotask company (the company index already resolves CSP names) → Orbit link.
  const companies = useCompanies();
  const companyOf = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of companies.data?.companies ?? []) for (const n of c.csp?.customers ?? []) m.set(n, c.id);
    return m;
  }, [companies.data]);

  const customers = useMemo(() => {
    const groups = new Map<string, CspRow[]>();
    for (const r of visible) groups.set(r.customer, [...(groups.get(r.customer) ?? []), r]);
    const s = q.trim().toLowerCase();
    return [...groups.entries()]
      .filter(([name, subs]) => !s || name.toLowerCase().includes(s) || subs[0].accountManager.toLowerCase().includes(s))
      .map(([name, subs]) => ({
        name,
        subs,
        worst: worstStatus(subs),
        seats: subs.reduce((n, r) => n + r.quantity, 0),
        soonest: subs.filter((r) => r.daysLeft != null).sort((a, b) => a.daysLeft! - b.daysLeft!)[0] ?? null,
        accountManager: subs[0].accountManager,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [visible, q]);

  return (
    <div className="mx-auto w-full max-w-[1600px] px-4 py-5 md:px-6">
      <PageHeader title="Customers" count={dataset ? customers.length : null} isFetching={query.isFetching}
        subtitle="Every customer with NCE subscriptions, coloured by their most urgent renewal"
        actions={<>
          <LongExpiredToggle />
          <SearchInput withIcon placeholder="Customer or account manager" className="w-64" value={q} onValueChange={setQ} />
          <Button variant="outline" size="sm" onClick={() => exportIcs(visible)} disabled={!visible.length}>
            <CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> Export all to Outlook
          </Button>
        </>}
      />

      {query.isLoading ? (
        <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
      ) : !dataset ? <EmptyDataset /> : (
        <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4">
          {customers.length === 0 && <div className="col-span-full py-10 text-center text-sm text-muted-foreground">No customers match.</div>}
          {customers.map((c) => (
            <div key={c.name} role="button" tabIndex={0}
              onClick={() => navigate(cspLicencesView({ client: c.name }))}
              onKeyDown={(e) => { if (e.key === 'Enter') navigate(cspLicencesView({ client: c.name })); }}
              className="cursor-pointer rounded-lg border border-border bg-card p-4 shadow-sm transition-colors hover:border-ring hover:bg-muted/30">
              <div className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-soft text-xs font-semibold text-foreground">
                  {c.name.split(/\s+/).map((w) => w[0]).join('').toUpperCase().slice(0, 2)}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="line-clamp-2 text-sm font-semibold leading-snug text-foreground" title={c.name}>{c.name}</div>
                  {c.accountManager && <div className="truncate text-xs text-muted-foreground">{c.accountManager}</div>}
                </div>
                <StatusBadge status={c.worst} />
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                <Stat value={c.subs.length} label="Subs" />
                <Stat value={c.seats} label="Seats" />
                <Stat value={c.soonest ? `${c.soonest.daysLeft}d` : '—'} label="Soonest"
                  className={c.worst === 'expired' ? 'text-destructive' : c.worst === 'expiring' ? 'text-warning' : 'text-success'} />
              </div>
              <div className="mt-3 flex items-center justify-between">
                {companyOf.has(c.name) ? <OrbitLink companyId={companyOf.get(c.name)} className="text-xs text-muted-foreground hover:text-foreground">Orbit →</OrbitLink> : <span />}
                <Button variant="ghost" size="sm" className="h-7 text-xs"
                  onClick={(e) => { e.stopPropagation(); exportIcs(all.filter((r) => r.customer === c.name), c.name); }}>
                  <CalendarPlus className="mr-1 h-3 w-3" /> Outlook
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ value, label, className }: { value: string | number; label: string; className?: string }) {
  return (
    <div className="rounded-md bg-muted/50 py-1.5">
      <div className={cn('text-base font-semibold tabular-nums text-foreground', className)}>{value}</div>
      <div className="text-[10.5px] uppercase tracking-wider text-muted-foreground">{label}</div>
    </div>
  );
}
